import { Injectable, signal } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SpinnerService {
  loaderCount = 0;
  isSpinnerRunning = signal<boolean>(false);
  //Button ids currently showing a loader (supports multiple simultaneous loaders).
  activeButtonIds = signal<readonly string[]>([]);

  private buttonLoaderCounts = new Map<string, number>();

  /** Full-screen spinner was hidden while a user prompt (e.g. take-control) is open. */
  #pausedFullScreen = false;
  /** Button loader ids hidden while a user prompt is open; counts stay intact. */
  #pausedButtonIds: readonly string[] | null = null;

  handleSpinnerStart(buttonId?: string | null) {
    if (buttonId) {
      const count = (this.buttonLoaderCounts.get(buttonId) ?? 0) + 1;
      this.buttonLoaderCounts.set(buttonId, count);

      if (!this.activeButtonIds().includes(buttonId) && !this.#pausedButtonIds?.includes(buttonId)) {
        this.activeButtonIds.update(ids => [...ids, buttonId]);
      }
      return;
    }

    this.loaderCount++;
    if (!this.#pausedFullScreen) {
      this.isSpinnerRunning.set(true);
    }
  }

  handleSpinnerStop(buttonId?: string | null) {
    if (buttonId) {
      const count = this.buttonLoaderCounts.get(buttonId);
      if (!count) {
        return;
      }

      if (count <= 1) {
        this.buttonLoaderCounts.delete(buttonId);
        this.activeButtonIds.update(ids => ids.filter(id => id !== buttonId));
      } else {
        this.buttonLoaderCounts.set(buttonId, count - 1);
      }
      return;
    }

    if (this.loaderCount > 0) {
      this.loaderCount--;

      if (this.loaderCount === 0) {
        this.isSpinnerRunning.set(false);
      }
    }
  }

  /**
   * Hide loaders while a blocking user prompt is open (e.g. deal take-control).
   * Keeps start/stop counts intact so the in-flight request can resume or finalize cleanly.
   */
  pauseForUserPrompt(): void {
    if (this.loaderCount > 0 && this.isSpinnerRunning()) {
      this.#pausedFullScreen = true;
      this.isSpinnerRunning.set(false);
    }

    const buttonIds = this.activeButtonIds();
    if (buttonIds.length) {
      this.#pausedButtonIds = buttonIds;
      this.activeButtonIds.set([]);
    }
  }

  /** Show loaders again after the user accepts the prompt and the request continues. */
  resumeAfterUserPrompt(): void {
    if (this.#pausedFullScreen) {
      this.#pausedFullScreen = false;
      if (this.loaderCount > 0) {
        this.isSpinnerRunning.set(true);
      }
    }

    if (this.#pausedButtonIds) {
      const stillActive = this.#pausedButtonIds.filter(id => (this.buttonLoaderCounts.get(id) ?? 0) > 0);
      this.#pausedButtonIds = null;
      if (stillActive.length) {
        this.activeButtonIds.update(ids => [...new Set([...ids, ...stillActive])]);
      }
    }
  }

  /** Drop pause state without forcing loaders back on (request is ending / was cancelled). */
  clearPromptPause(): void {
    this.#pausedFullScreen = false;
    this.#pausedButtonIds = null;
  }

  isButtonLoading(buttonId: string): boolean {
    return this.activeButtonIds().includes(buttonId);
  }
}
